import { MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SuperAdminPendingApprovalJobs } from "../model/super_admin_jobs_model";

interface SuperAdminJobPendingApprovalCardProps {
  jobPending: SuperAdminPendingApprovalJobs;
  openMenuJobId: string | null;
  onToggleMenu: (jobId: string) => void;
  onAction: (jobId: string, action: "approve" | "reject") => void;
}

export default function SuperAdminJobPendingApprovalCard({
  jobPending,
  openMenuJobId,
  onToggleMenu,
  onAction,
}: SuperAdminJobPendingApprovalCardProps & { viewMode?: string }) {
  const isOpen = openMenuJobId === jobPending.job_id;
  const statusStyles: Record<string, string> = {
    Active: "bg-green-100 text-green-700",
    Draft: "bg-gray-100 text-gray-700",
    Closed: "bg-red-100 text-red-700",
    published: "bg-yellow-100 text-yellow-700",
  };
  const navigate = useNavigate();
  const handleViewJobDetails = (jobPending: SuperAdminPendingApprovalJobs) => {
    navigate(`/super-admin/PendingJobDetails/${jobPending.request_id}`, {
      state: jobPending,
    });
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-start justify-between">
          <div className="min-w-0 pr-3">
            <h2 className="text-base font-semibold text-gray-900 truncate">
              {jobPending.job_title || "Untitled Job"}
            </h2>
            {jobPending.company_name && (
              <p className="mt-1 text-xs font-medium text-gray-500 truncate">
                {jobPending.company_name}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 text-xs rounded-full font-medium ${statusStyles[jobPending.status] || "bg-blue-100 text-blue-700"
                  }`}
              >
                {jobPending.status}
              </span>
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => onToggleMenu(jobPending.job_id)}
                className="p-2 hover:bg-gray-100 rounded-full text-[0px] text-gray-500 hover:text-gray-800"
                aria-label="Open job actions"
              >
                <MoreVertical className="h-4 w-4 text-gray-500" />⋮
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg py-1 z-20">
                  <button
                    type="button"
                    onClick={() => {
                      onAction(jobPending.request_id, "approve");
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Approve
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onAction(jobPending.request_id, "reject");
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <div>
            <span className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
              {jobPending.job_description}
            </span>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="flex flex-col gap-1">
              <span className="text-blue-600 font-medium">
                {jobPending?.department}
              </span>

              <span className="text-xs text-gray-500">
                {jobPending.location}
              </span>
            </div>

            <button
              type="button"
              onClick={() => handleViewJobDetails(jobPending)}
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            >
              View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
